/**
 * Three fights, and the money on each of them.
 *
 * The campaign has offered exactly one opponent since step 49 — the rung above,
 * or the card once you were a contender — and a ladder with one door is a
 * ladder you are walked up rather than one you climb. There was no such thing
 * as a bad night's matchmaking, because there was no matchmaking.
 *
 * So: three. **A safe fight below you, the rung above, and one well over your
 * head.** They are not three difficulties of the same fight; they are three
 * different bets, and the purse is what makes them different. A win against
 * somebody under you moves you nowhere (`settle` only moves the winner when the
 * loser was above) and pays the least. The reach pays several times that and
 * will probably cost you a place.
 *
 * And **ducking the reach costs heat**. A fighter who takes the easy one twice
 * is a fighter the room stops turning up for, and heat is already the measure of
 * how worth watching somebody is. That is a rule about the *crowd* rather than
 * a punishment: the thresholds are in `cards.json` and the size of the hit is in
 * `heat.json` with the other nine, because a heat number that lived somewhere
 * else would be a heat number nothing validates.
 *
 * Pure. It is handed a ladder and a gate and returns plain data; `campaign.js`
 * owns the offer, so the act-ending fight is still chosen behind the gate
 * (gotcha 47) and there is no door into it that skips one.
 */

import CARDS from './cards.json';
import HEAT from '../core/heat.json';
import { sampleCurve, validateCurve } from '../core/heat.js';

export class CardsConfigError extends Error {}

/** The three, in the order they are offered and in the order they are resolved. */
const SLOT_IDS = Object.freeze(['safe', 'near', 'reach']);

/**
 * Throws on an offer that could not mean what it says.
 *
 * The shape checks are the cheap half. What matters is that the three cards are
 * still three *different bets*: a safe fight that is not below you is not safe,
 * a reach one rung up is not a reach, and a purse curve that is not 1.0 at the
 * resting heat makes every number in the file a lie about what a fight pays.
 */
export function validateCards(config = CARDS, { heat = HEAT } = {}) {
  const { slots, purse, ducking } = config ?? {};

  if (!Array.isArray(slots) || slots.length !== SLOT_IDS.length) {
    throw new CardsConfigError(`there are ${slots?.length ?? 0} cards; the mechanic is three`);
  }
  slots.forEach((slot, at) => {
    if (slot?.id !== SLOT_IDS[at]) {
      throw new CardsConfigError(
        `card ${at + 1} is ${JSON.stringify(slot?.id)}; want "${SLOT_IDS[at]}"`
      );
    }
    if (!Number.isInteger(slot.offset)) {
      throw new CardsConfigError(`"${slot.id}" is offset ${JSON.stringify(slot.offset)}; want ranks`);
    }
    if (!slot.describe) throw new CardsConfigError(`"${slot.id}" does not say what it is`);
  });

  const by = Object.fromEntries(slots.map((slot) => [slot.id, slot]));
  // A rank *below* you is a larger number, so the offsets run downwards.
  if (!(by.safe.offset > 0)) {
    throw new CardsConfigError(`the safe card is offset ${by.safe.offset}; it has to be below you`);
  }
  if (!(by.reach.offset < 0)) {
    throw new CardsConfigError(`the reach card is offset ${by.reach.offset}; it has to be above you`);
  }
  if (!(by.safe.offset > by.near.offset && by.near.offset > by.reach.offset)) {
    throw new CardsConfigError(
      `the offsets are ${slots.map((s) => s.offset).join(', ')}; safe must be below near must be below reach`
    );
  }
  // "Well above" is the whole of what the third card is. One rung past the near
  // fight is the near fight with a different label on it.
  if (!(by.near.offset - by.reach.offset >= 2)) {
    throw new CardsConfigError(
      `the reach is ${by.near.offset - by.reach.offset} rung past the near fight; want at least 2`
    );
  }

  if (!(purse?.bottom > 0)) {
    throw new CardsConfigError(`purse.bottom is ${JSON.stringify(purse?.bottom)}; want money`);
  }
  if (!(purse.top > purse.bottom)) {
    throw new CardsConfigError(
      `purse.top (${purse.top}) is not above purse.bottom (${purse.bottom}); the ladder would pay flat`
    );
  }
  if (!(purse.act >= 1)) {
    throw new CardsConfigError(`purse.act is ${JSON.stringify(purse.act)}; the card cannot pay less`);
  }
  if (!(purse.round >= 1) || !Number.isInteger(purse.round)) {
    throw new CardsConfigError(`purse.round is ${JSON.stringify(purse.round)}; want whole money`);
  }
  validateCurve(purse.heat, heat.max);
  // Same load-bearing property the momentum curve has (§4b): **exactly 1.0 at
  // the resting heat**, so `bottom` and `top` are readable as what a fight
  // actually pays rather than as an input to an invisible multiplier.
  const resting = sampleCurve(purse.heat, heat.baseline);
  if (Math.abs(resting - 1) > 1e-9) {
    throw new CardsConfigError(
      `the purse curve is ${resting} at the resting heat of ${heat.baseline}; it has to be 1`
    );
  }

  if (!Number.isInteger(ducking?.after) || ducking.after < 0) {
    throw new CardsConfigError(`ducking.after is ${JSON.stringify(ducking?.after)}; want a count`);
  }
  if (!Number.isInteger(ducking?.every) || ducking.every < 1) {
    throw new CardsConfigError(`ducking.every is ${JSON.stringify(ducking?.every)}; want at least 1`);
  }

  return config;
}

validateCards(CARDS);

export { CARDS };

/**
 * What one fight pays.
 *
 * Two things move it and they are the two things the player can see: **who they
 * are fighting**, as a position on the ladder rather than a rank number (a
 * thirteen-strong ladder and a thirty-strong one should pay the same for "the
 * one at the top"), and **their own heat**, because a purse is what somebody is
 * willing to pay to watch you.
 */
export function purseFor({ rank, size, act = false, heat = HEAT.baseline, config = CARDS } = {}) {
  const { bottom, top, round } = config.purse;
  // 0 at the foot of the ladder, 1 at the top of it.
  const standing = size > 1 ? (size - rank) / (size - 1) : 1;
  const base = bottom + (top - bottom) * Math.min(Math.max(standing, 0), 1);
  const paid = base * sampleCurve(config.purse.heat, heat) * (act ? config.purse.act : 1);
  return Math.max(round, Math.round(paid / round) * round);
}

/**
 * The three cards, made.
 *
 * Resolved **reach first, then near, then safe** — the file's own order, read
 * backwards. The reach has the strongest claim on a particular rank and the
 * safe card the weakest, so when the ideal ranks collide (near the top of the
 * ladder they always do) the card that is the whole point of the offer keeps
 * the fighter it wanted.
 *
 * Every card is a different person. Three cards showing two fighters is two
 * cards with a spare.
 */
export function buildOffer({ ladder, gate, act = null, heat = HEAT.baseline, config = CARDS } = {}) {
  if (!ladder || !gate) return [];
  const at = gate.rank;
  const size = ladder.size;
  const taken = new Set([ladder.playerId]);

  /** The nearest fighter to `ideal` who is not already on a card. */
  function nearest(ideal) {
    const want = Math.min(Math.max(ideal, 1), size);
    for (let out = 0; out < size; out += 1) {
      for (const rank of out === 0 ? [want] : [want - out, want + out]) {
        if (rank < 1 || rank > size) continue;
        const who = ladder.at(rank);
        if (who && !taken.has(who.id)) return who;
      }
    }
    return null;
  }

  const made = new Map();
  for (const slot of [...config.slots].reverse()) {
    // The act-ending fight is the reach, when there is one to be had. It is
    // literally the fight furthest above the player — whoever is holding the
    // top — and giving it a card of its own would have been a fourth door into
    // the thing the gate exists to guard.
    const forTheCard = Boolean(slot.id === 'reach' && gate.open && act?.opponent);
    const who = forTheCard ? ladder.find(act.opponent.id) : nearest(at + slot.offset);
    if (!who) continue;
    taken.add(who.id);
    made.set(slot.id, {
      slot: slot.id,
      /**
       * What this card *is*, not where the fighter on it is standing. The slot
       * describes name the slot and `climb` carries the direction, because a
       * label reading "the rung above" over somebody two places below — which
       * is what the near card resolves to once the player is holding rank one
       * and there is nothing above them — is a mechanic the player reads as a
       * bug. The act says so in its own words, out of `ladder.json`.
       */
      describe: forTheCard ? act.describe ?? slot.describe : slot.describe,
      kind: forTheCard ? 'act' : 'circuit',
      /** The act's name when it is the act, so a screen has something to show. */
      name: forTheCard ? act.name : who.name,
      opponent: who,
      /** How far above the player they are. Negative for the safe card. */
      climb: at - who.rank,
      purse: purseFor({ rank: who.rank, size, act: forTheCard, heat, config }),
      /**
       * Where it is fought — the venue, the arena that loads for it, who holds
       * the room and whether it is on the line. See `venues.js`.
       */
      venue: venueCard(ladder, who.id),
      /**
       * The one the campaign points at when nobody picks. The act when it is
       * open, and the rung above otherwise — which is exactly what `next`
       * offered before there were three of these.
       */
      pointed: forTheCard || (!gate.open && slot.id === 'near'),
    });
  }

  // Back into the file's order, which is the order they are shown in.
  return Object.freeze(
    config.slots.map((slot) => made.get(slot.id)).filter(Boolean).map((card) => Object.freeze(card))
  );
}

/**
 * Where a card is fought, and what is on the line there for the player.
 *
 * `stake` is `take` when the opponent holds the room — win and it is yours —
 * `defend` when the player does, and null when it is held by somebody who is
 * not in the fight, or by nobody the fight concerns. Null altogether on a
 * ladder with no venues.
 */
export function venueCard(ladder, opponentId) {
  const venue = ladder.venueFor?.(ladder.playerId, opponentId) ?? null;
  if (!venue) return null;
  const stake = venue.holder === opponentId ? 'take' : venue.holder === ladder.playerId ? 'defend' : null;
  return Object.freeze({ ...venue, stake });
}

/**
 * Whether turning the reach down this time costs anything.
 *
 * `after` free refusals, then one every `every`. Free ones first because a
 * fighter is allowed to pick their spots — it is doing it *repeatedly* that
 * empties the room, and a crowd that punished the first duck would be punishing
 * matchmaking rather than cowardice.
 */
export function ducking(count, config = CARDS) {
  const { after, every } = config.ducking;
  const past = count - after;
  return Object.freeze({
    count,
    charged: past > 0 && past % every === 0,
    /** Refusals still going free. Zero once the crowd has started counting. */
    free: Math.max(0, after - count),
  });
}
